import { useCallback, useState } from 'react';
import { apiClient } from '@/api/api-client';
import { Button } from '@/components/ui/button';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/utils';
import type { LeaderboardCopy } from '../leaderboard-copy';
import type { ViewMode } from '../leaderboard.types';

interface ExportButtonProps {
  copy: LeaderboardCopy;
  viewMode: ViewMode;
  language?: string;
}

interface ExportRow {
  rank: number;
  username: string;
  displayName: string;
  contributions: number;
}

const HEADERS = ['Rank', 'Username', 'Display Name', 'Contributions'];

function toCSV(rows: ExportRow[]): string {
  const csvRows = rows.map((r) => [r.rank, r.username, `"${String(r.displayName ?? '').replace(/"/g, '""')}"`, r.contributions].join(','));
  return [HEADERS.join(','), ...csvRows].join('\n');
}

/**
 * Full board as CSV, admins only (the export routes are admin-guarded). The
 * page shows the top 100; the export has every contributor.
 */
export function ExportButton({ copy, viewMode, language }: ExportButtonProps) {
  const { user, t } = useApp();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const handleExport = useCallback(async () => {
    setLoading(true);
    try {
      const path =
        viewMode === 'language' && language
          ? `/users/stats/leaderboard/${encodeURIComponent(language)}/export`
          : `/users/stats/leaderboard/export`;
      const { data } = await apiClient.get<ExportRow[]>(path);
      if (!Array.isArray(data) || data.length === 0) {
        toast({ title: t.leaderboardNoDataExport });
        return;
      }
      const blob = new Blob([toCSV(data)], { type: 'text/csv;charset=utf-8;' });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `leaderboard-${viewMode}${language ? `-${language}` : ''}.csv`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast({ title: copy.exportError, description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [viewMode, language, toast, t.leaderboardNoDataExport, copy.exportError]);

  if (!isAdmin) return null;

  return (
    <Button variant="outline" size="sm" onClick={handleExport} disabled={loading}>
      <Icon name={loading ? 'loader2' : 'download'} className={cn(loading && 'animate-spin')} />
      {loading ? copy.exporting : copy.export}
    </Button>
  );
}
